import {
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  IconButton,
  Input,
  NumberInput,
  NumberInputField,
  Select,
} from "@chakra-ui/react";
import { useField } from "formik";
import React from "react";
import { RiAddLine, RiDeleteBinLine } from "react-icons/ri";
import { BAND_COLOR_SCHEMES, BandFormik, newBand } from "./bandFields";

type Props = {
  name: string;
  label: string;
  helperText: string;
  countLabel: string;
};

export const BandListEditor: React.FC<Props> = ({
  name,
  label,
  helperText,
  countLabel,
}) => {
  const [field, meta, helpers] = useField<BandFormik[]>(name);
  const bands = field.value;

  const update = (index: number, patch: Partial<BandFormik>) => {
    helpers.setValue(
      bands.map((band, i) => (i === index ? { ...band, ...patch } : band))
    );
  };

  const remove = (index: number) => {
    helpers.setValue(bands.filter((_band, i) => i !== index));
  };

  return (
    <FormControl isInvalid={typeof meta.error === "string"}>
      <FormLabel>{label}</FormLabel>
      <FormHelperText marginBottom={2}>{helperText}</FormHelperText>

      <Flex direction="column" gap={2}>
        {bands.map((band, index) => (
          <Flex key={band.key} gap={2} alignItems="flex-end" wrap="wrap">
            <FormControl flex="2" minWidth="10rem">
              <FormLabel fontSize="xs">Label</FormLabel>
              <Input
                size="sm"
                value={band.label}
                placeholder="e.g. Seats available"
                onChange={(event) =>
                  update(index, { label: event.target.value })
                }
              />
            </FormControl>

            <FormControl flex="1" minWidth="7rem">
              <FormLabel fontSize="xs">Color</FormLabel>
              <Select
                size="sm"
                value={band.colorScheme}
                onChange={(event) =>
                  update(index, {
                    colorScheme: event.target
                      .value as BandFormik["colorScheme"],
                  })
                }
              >
                {BAND_COLOR_SCHEMES.map((color) => (
                  <option key={color} value={color}>
                    {color}
                  </option>
                ))}
              </Select>
            </FormControl>

            <FormControl flex="1" minWidth="7rem">
              <FormLabel fontSize="xs">{countLabel}</FormLabel>
              <NumberInput
                size="sm"
                min={0}
                value={band.minCount}
                onChange={(_string, value) =>
                  update(index, { minCount: Number.isNaN(value) ? 0 : value })
                }
              >
                <NumberInputField />
              </NumberInput>
            </FormControl>

            <IconButton
              size="sm"
              variant="ghost"
              colorScheme="gray"
              aria-label={`Remove ${band.label || "band"}`}
              icon={<RiDeleteBinLine />}
              isDisabled={bands.length <= 1}
              onClick={() => remove(index)}
            />
          </Flex>
        ))}
      </Flex>

      {typeof meta.error === "string" ? (
        <FormErrorMessage>{meta.error}</FormErrorMessage>
      ) : null}

      <Button
        size="sm"
        variant="outline"
        colorScheme="gray"
        marginTop={3}
        leftIcon={<RiAddLine />}
        onClick={() => helpers.setValue([...bands, newBand()])}
      >
        Add band
      </Button>
    </FormControl>
  );
};
